// ---------------------------------------------------------------------------
// Activity domain types
// Ported from trusstor_dashboard/shared/interfaces/
// ---------------------------------------------------------------------------

import { z } from 'zod';
import { configAreaSchema } from './floor.js';
import type { IConfigArea } from './floor.js';
import type { IMinimizedProfession } from './profession.js';
import type { IIssue } from './issue.js';

// ---------------------------------------------------------------------------
// ActivityGroupStatus
// (from trusstor_dashboard/shared/interfaces/ActivityGroupStatus.enum.ts)
// ---------------------------------------------------------------------------

export enum ActivityGroupStatus {
	planned = 'planned',
	inProgress = 'inProgress',
	delayed = 'delayed',
	complete = 'complete',
	overdue = 'overdue',
	deleted = 'deleted',
}

// ---------------------------------------------------------------------------
// SequenceTypeEnum
// (from trusstor_dashboard/shared/interfaces/SequenceType.enum.ts)
// ---------------------------------------------------------------------------

export enum SequenceTypeEnum {
	'APARTMENT' = 'apartment',
	'GARAGE' = 'garage',
	'GENERAL' = 'general',
	'CORE' = 'core',
	'LOBBY' = 'lobby',
	'STRUCTURE' = 'structure',
	'OFFICES' = 'offices',
	'HOTEL_ROOMS' = 'hotelRooms',
	'PRODUCTION_LINES' = 'productionLines',
	'COMMERCIALS' = 'commercials',
	'WET_ROOMS' = 'wetRooms',
	'MECHANICAL_ROOMS' = 'mechanicalRooms',
	'FACADE' = 'facade',
	'ELEVATORS' = 'elevators',
	'STAIRS' = 'stairs',
	'Kitchens' = 'kitchens',
	'Toilets' = 'toilets',
}

// ---------------------------------------------------------------------------
// AreaSequenceItemStatus
// (from trusstor_dashboard/shared/interfaces/AreaSequenceItemStatus.enum.ts)
// ---------------------------------------------------------------------------

export enum AreaSequenceItemStatus {
	unplanned = 'unplanned',
	planned = 'planned',
	delayed = 'delayed',
	delayedOverdue = 'delayedOverdue',
	inProgress = 'inProgress',
	overdue = 'overdue',
	readyForReview = 'readyForReview',
	complete = 'complete',
}

// ---------------------------------------------------------------------------
// StatusChannel (inline)
// (from trusstor_dashboard/shared/interfaces/statusChannel.type.ts)
// ---------------------------------------------------------------------------

export const statusChannelSchema = z.enum(['manual', 'automatic']);
export type StatusChannel = z.infer<typeof statusChannelSchema>;

// ---------------------------------------------------------------------------
// areaSequenceItemSchema / IAreaSequenceItem
// (from trusstor_dashboard/shared/interfaces/IAreaSequenceItem.ts)
// ---------------------------------------------------------------------------

export const areaSequenceItemSchema = z
	.object({
		_id: z.string().optional(),
		projectId: z.string(),
		sequenceId: z.string(),
		sequenceItemId: z.string(),
		sequenceType: z.enum(SequenceTypeEnum),
		area: configAreaSchema,
		activityId: z.string().nullable().optional(),
		activityGroupId: z.string().nullable().optional(),
		status: z.enum(AreaSequenceItemStatus),
		statusChannel: statusChannelSchema,
		completeDate: z.date().nullable().optional(),
		lastStatusUpdateDate: z.date().optional(),
		lastUsernameUpdated: z.string().optional(),
	})
	.passthrough();

export type IAreaSequenceItem = z.infer<typeof areaSequenceItemSchema>;

// ---------------------------------------------------------------------------
// IAreaSequenceItemWithActivityStatus
// (from trusstor_dashboard/shared/interfaces/IMainPageWidgetActivityData.ts)
// ---------------------------------------------------------------------------

export interface IAreaSequenceItemWithActivityStatus extends IAreaSequenceItem {
	groupStatus?: ActivityGroupStatus;
}

// ---------------------------------------------------------------------------
// IConfigAreaWithAreaSequenceItem
// (from trusstor_dashboard/shared/interfaces/IConfigAreaWithAreaSequenceItem.ts)
// ---------------------------------------------------------------------------

export interface IConfigAreaWithAreaSequenceItem extends IConfigArea {
	areaSequenceItemId?: string;
}

// ---------------------------------------------------------------------------
// IAssignedActivityArea
// (from trusstor_dashboard/shared/interfaces/IAssignedActivityArea.ts)
// ---------------------------------------------------------------------------

export interface IAssignedActivityArea extends IConfigAreaWithAreaSequenceItem {
	accumulatedWorkTime: number;
	lastUpdatedWorkTime: number;
	totalWorkDaysNeededForArea: number;
}

// ---------------------------------------------------------------------------
// IMainPageWidgetFloor
// (from trusstor_dashboard/shared/interfaces/IMainPageWidgetFloor.ts)
// Extends IBaseFloor (floorId, floorNick)
// ---------------------------------------------------------------------------

export interface IMainPageWidgetFloor {
	floorId: string;
	floorNick: string;
	accumulatedWorkTime: number;
	shortFloorNick: string;
}

// ---------------------------------------------------------------------------
// IMainPageWidgetActivityData<T>
// (from trusstor_dashboard/shared/interfaces/IMainPageWidgetActivityData.ts)
// ---------------------------------------------------------------------------

export interface IMainPageWidgetActivityData<T extends IMinimizedProfession> {
	description: string;
	originalStartDate: Date;
	originalEndDate: Date;
	startDate: Date;
	endDate: Date;
	profession: T;
	groupStatus: ActivityGroupStatus;
	groupId: string;
	floors: IMainPageWidgetFloor[];
	areas: IAssignedActivityArea[];
	areaSequenceItems: IAreaSequenceItemWithActivityStatus[];
	issues?: IIssue[];
	assignee?: string | null;
}
